import React, { Component } from 'react';
// TODO: switch to use `@atlassian/jira-intl` later due to this issue https://jdog.jira-dev.com/browse/MAGMA-443
import compact from 'lodash/compact';
import isString from 'lodash/isString';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { ErrorHash } from '@atlassian/jira-errors-handling/src/ui/index.tsx';
import type { CommonFlagAction } from '@atlassian/jira-flags/src/common/ui/components/common-flag';
import { JiraCommonFlag } from '@atlassian/jira-flags/src/common/ui/components/common-flag-v2';
import FlagGroup from '@atlassian/jira-flags/src/common/ui/components/group-flag';
import type {
	FlagMessage,
	FlagInstance,
	FlagInstanceId,
	FlagAction,
	FlagConfiguration,
	FlagCustom,
	FlagComponent as FlagComponentType,
} from '@atlassian/jira-flags/src/services/types';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { ContextualAnalyticsData, MODAL } from '@atlassian/jira-product-analytics-bridge';

type Props = {
	flags: FlagInstance[];
	onDismissed: (id: FlagInstanceId) => void;
	intl: IntlShape;
};

/**
 * Manages and renders flag notifications, supporting dynamic appearances and behaviors. Incorporates
 * internationalization for adaptable message formatting and handles user interactions through
 * configurable actions. Designed to display transient, informative messages to users in a visually
 * consistent manner.
 */
// eslint-disable-next-line jira/react/no-class-components
class Flags extends Component<Props> {
	formatMessage(message: FlagMessage | string): string {
		const {
			intl: { formatMessage },
		} = this.props;
		if (Array.isArray(message)) {
			return formatMessage(...message);
		}
		if (isString(message)) {
			return String(message);
		}
		return formatMessage(message);
	}

	formatActions(actions?: FlagAction[]): CommonFlagAction[] | undefined {
		return actions
			? actions.map((action) => ({
					onClick: action.onClick,
					content: this.formatMessage(action.content),
					href: action.href,
					target: action.target,
				}))
			: undefined;
	}

	renderConfigurableFlag(id: FlagInstanceId, flagConfiguration: FlagConfiguration) {
		const {
			type,
			title,
			description,
			isAutoDismiss,
			actions,
			appearance,
			testId,
			error,
			icon,
			onDismissed,
			delayAnnouncement,
			messageId,
			messageType,
		} = flagConfiguration;
		const defaultedIsAutoDismiss =
			isAutoDismiss === undefined ? type === 'success' || type === 'info' : isAutoDismiss;
		const isValidType = ['success', 'error', 'info', 'warning'].includes(type);
		let formattedDescription = description && this.formatMessage(description);
		if (error) {
			// @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'string'.
			formattedDescription = (
				<>
					<>{formattedDescription}</>
					<ErrorHash error={error} />
				</>
			);
		}
		if (isValidType) {
			return (
				<JiraCommonFlag
					key={id}
					id={id}
					flagType={type}
					icon={icon}
					title={this.formatMessage(title)}
					description={formattedDescription}
					isAutoDismiss={defaultedIsAutoDismiss}
					actions={this.formatActions(actions)}
					appearance={appearance}
					testId={testId}
					onDismissed={onDismissed}
					messageId={messageId}
					messageType={messageType}
					{...(delayAnnouncement && { delayAnnouncement })}
				/>
			);
		}
		return null;
	}

	render() {
		const { flags, onDismissed } = this.props;
		return (
			<ContextualAnalyticsData sourceName="flag" sourceType={MODAL}>
				<FlagGroup onDismissed={onDismissed}>
					{compact(
						flags.map((flagInstance: FlagInstance) => {
							const { id, flag } = flagInstance;

							// @ts-expect-error - TS2339 - Property 'render' does not exist on type 'Flag'.
							if (flag.render !== undefined) {
								// @ts-expect-error - TS2322 - Type 'Flag' is not assignable to type 'FlagCustom'.
								const flagCustom: FlagCustom = flag;
								return flagCustom.render({
									key: id,
								});
							}

							// @ts-expect-error - TS2339 - Property 'type' does not exist on type 'Flag'.
							if (flag.type !== undefined) {
								// @ts-expect-error - TS2322 - Type 'Flag' is not assignable to type 'FlagConfiguration'.
								const flagConfiguration: FlagConfiguration = flag;
								const comp = this.renderConfigurableFlag(id, flagConfiguration);
								if (comp) {
									return comp;
								}
								log.safeErrorWithoutCustomerData(
									'flags.view',
									`There was a problem rendering a flag of type: ${flagConfiguration.type}`,
								);
								return null;
							}

							// @ts-expect-error - TS2322 - Type 'Flag' is not assignable to type 'FlagComponent'.
							const FlagComponent: FlagComponentType = flag;
							return <FlagComponent key={id} id={id} onDismissed={onDismissed} />;
						}),
					)}
				</FlagGroup>
			</ContextualAnalyticsData>
		);
	}
}
export default injectIntl(Flags);
